import moment from 'moment';
import { memo } from 'react';
// components
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Row from 'react-bootstrap/Row';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
// constants
import { TRANSFER_RECONCILE_BADGE, TRANSFER_STATUS_BADGE } from '../../../../../constants/fundings';
// API
import { toast } from 'react-toastify';
import { makeRequest } from '../../../../../utils/makeRequest';
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';

function NewFundingTransfersBoxForm({
  showTransferEvents,
  toggleTransferEvent,
  event,
  fundingId,
  fundingStatus,
  consumerIsTest,
  refreshFundingData,
  setSpinner,
}) {
  const { apiOriginFunding } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const sendTransferAction = async ({ askForConfirmation, url, payload, message }) => {
    const confirmation = askForConfirmation ? window.confirm('Are you sure?') : true;

    setSpinner(true);
    if (confirmation) {
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginFunding}/${url}`,
        method: 'POST',
        data: JSON.stringify(payload),
      };

      try {
        await makeRequest(config);
        await refreshFundingData();

        toast.success(message);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  const getReconcilled = transfer => {
    if (transfer.forceReconcile && transfer.reconTransactionId) {
      return 'YES_FORCE';
    }
    if (transfer.forceReconcile) {
      return 'FORCE';
    }
    if (transfer.reconTransactionId) {
      return 'YES';
    }

    return 'NO';
  };

  return (
    <div>
      <table className="bg-light">
        <tbody>
          {event.transfers.map(transfer => (
            <tr key={transfer.id}>
              <td>
                <div className="bo-border-container w-100">
                  <table>
                    <tbody>
                      <tr>
                        <td className="bo-w-150">Transfer ID:</td>
                        <td className="bo-w-250">{transfer.uuid}</td>
                        <td className="bo-w-150">
                          {consumerIsTest && transfer.status === 'CREATED' && (
                            <Button
                              className="bo-common-inline-button bo-text-sm"
                              onClick={() =>
                                sendTransferAction({
                                  url: 'ach_test_webhook',
                                  payload: {
                                    uuid: transfer.uuid,
                                    code: 'OK',
                                    description: '',
                                  },
                                  message: 'Transfer success event sent!',
                                })
                              }
                            >
                              Send transfer success
                            </Button>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Timestamp:</td>
                        <td>
                          {moment
                            .utc(transfer.createTimestamp)
                            .local()
                            .format('MMM DD, YYYY H:mm (UTCZ)')}
                        </td>
                        <td>
                          {consumerIsTest && transfer.status === 'CREATED' && (
                            <Button
                              className="bo-common-inline-button bo-text-sm"
                              onClick={() =>
                                sendTransferAction({
                                  url: 'ach_test_webhook',
                                  payload: {
                                    uuid: transfer.uuid,
                                    code: 'ERROR',
                                    description: 'Test Transfer failue event',
                                  },
                                  message: 'Transfer failure event sent!',
                                })
                              }
                            >
                              Send transfer failure
                            </Button>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Provider:</td>
                        <td>{transfer.provider}</td>
                      </tr>
                      <tr>
                        <td>Type:</td>
                        <td>{transfer.type}</td>
                      </tr>
                      <tr>
                        <td>Status:</td>
                        <td className="d-flex align-items-center gap-3">
                          {TRANSFER_STATUS_BADGE[transfer.status] && (
                            <Badge
                              className="bo-common-inline-badge"
                              bg={TRANSFER_STATUS_BADGE[transfer.status]?.color}
                            >
                              {TRANSFER_STATUS_BADGE[transfer.status]?.text}
                            </Badge>
                          )}
                          {transfer.provider === 'TABA_PAY' &&
                            (transfer.type === 'ACH' || transfer.type === 'SAME_DAY_ACH') &&
                            (transfer.status === 'CREATED' || transfer.status === 'COMPLETED') && (
                              <>
                                {transfer.status === 'CREATED' && (
                                  <Button
                                    className="bo-common-inline-button bo-text-sm"
                                    onClick={() =>
                                      sendTransferAction({
                                        askForConfirmation: true,
                                        url: 'ach_tabapay_completed',
                                        payload: { uuid: transfer.uuid },
                                        message: 'Transfer completed!',
                                      })
                                    }
                                  >
                                    Complete
                                  </Button>
                                )}
                                <Button
                                  className="bo-common-inline-button bo-text-sm"
                                  onClick={() =>
                                    sendTransferAction({
                                      askForConfirmation: true,
                                      url: 'ach_tabapay_failed',
                                      payload: {
                                        uuid: transfer.uuid,
                                        reason: 'Manual Failure by Admin',
                                      },
                                      message: 'Transfer failed!',
                                    })
                                  }
                                >
                                  Fail
                                </Button>
                                {transfer.status === 'CREATED' && (
                                  <Button
                                    className="bo-common-inline-button bo-text-sm"
                                    onClick={() =>
                                      sendTransferAction({
                                        askForConfirmation: true,
                                        url: 'ach_tabapay_cancelled',
                                        payload: { uuid: transfer.uuid },
                                        message: 'Transfer cancelled!',
                                      })
                                    }
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </>
                            )}
                        </td>
                      </tr>
                      <tr>
                        <td>Fee:</td>
                        <td>{transfer.fee || 'No Fee'}</td>
                      </tr>
                      <tr>
                        <td>Failure Reason:</td>
                        <OverlayTrigger
                          trigger={transfer.failureReason ? ['hover', 'focus'] : []}
                          placement="top"
                          delay={{ show: 200 }}
                          overlay={
                            <Tooltip className="tooltip-max-width">
                              {transfer.failureReason}
                            </Tooltip>
                          }
                        >
                          <td className="bo-max-w-1 text-truncate">{transfer.failureReason}</td>
                        </OverlayTrigger>
                      </tr>
                      <tr>
                        <td>Reconcilled:</td>
                        <td className="d-flex align-items-center gap-3">
                          <Badge
                            className="bo-common-inline-badge"
                            bg={TRANSFER_RECONCILE_BADGE[getReconcilled(transfer)]?.color}
                          >
                            {TRANSFER_RECONCILE_BADGE[getReconcilled(transfer)]?.text}
                          </Badge>
                          <Button
                            className="bo-common-inline-button bo-text-sm"
                            onClick={() =>
                              sendTransferAction({
                                askForConfirmation: true,
                                url: 'force_transfer_reconcile',
                                payload: {
                                  transferId: transfer.id,
                                  force: !transfer.forceReconcile,
                                },
                                message: 'Transfer force reconcile updated successfully!',
                              })
                            }
                          >
                            {transfer.forceReconcile ? 'Cancel Force' : 'Force Reconcile'}
                          </Button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <Row className="ms-0 mt-1">
                    <Button
                      className="bo-common-inline-button bo-text-sm text-start"
                      onClick={() => toggleTransferEvent(transfer.id)}
                    >
                      {showTransferEvents[transfer.id] ? 'Hide events' : 'Show events'}
                    </Button>
                  </Row>
                  {showTransferEvents[transfer.id] && (
                    <Row className="ms-1 mt-1">
                      <table>
                        <thead>
                          <tr>
                            <th className="bo-w-200">Timestamp</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transfer.events.map(tEvent => (
                            <tr key={tEvent.id}>
                              <td>
                                {moment
                                  .utc(tEvent.timestamp)
                                  .local()
                                  .format('MMM DD, YYYY H:mm (UTCZ)')}
                              </td>
                              <td>{tEvent.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Row>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {(fundingStatus === 'OPEN' || fundingStatus === 'DUE') &&
        (event.description.includes('Open') || event.description.includes('Top-up')) &&
        event.transfers.every(t => t.status === 'FAILED' || t.status === 'CANCELLED') && (
          <Button
            className="bo-common-button mt-2"
            onClick={() =>
              sendTransferAction({
                askForConfirmation: true,
                url: 'manual_transfer',
                payload: { fundingId, topupId: event.topupId || null },
                message: 'Manual transfer successfully created!',
              })
            }
          >
            Create Manual Transfer
          </Button>
        )}
    </div>
  );
}

export default memo(NewFundingTransfersBoxForm);
